import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  ArrowForward,
  Build,
  ElectricBolt,
  BatteryAlert,
  VisibilityOff,
  ErrorOutline
} from '@mui/icons-material';
import { selectCities } from '../../../redux/slices/citiesSlice';
import Select from '../../../components/Select';
import { SelectOption } from '../../../components/Select/interface';
import { updateScooterStatusAsync, updateScooterInfoAsync } from '../../../redux/slices/scootersSlice';
import { ScooterStatus } from '../../../pages/ZonesPage/interface';
import { Scooter } from '../../../resources/types/scootersTypes';

const allowConfirmChangeStatusCity = ['60d59e08bd31e836ae204610'];

interface Props {
  scooter?: Scooter;
  open: boolean;
  onSave: () => void;
  onBrokenStatusChange?: () => void;
}

const SettingsContent = ({ scooter, open, onSave, onBrokenStatusChange }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { items: cities } = useSelector(selectCities);

  const [city, setCity] = useState('');
  const [status, setStatus] = useState(scooter?.status ?? ScooterStatus.AVAILABLE);
  const [isReplaceableBattery, setIsReplaceableBattery] = useState(false);
  const [ignoreTrackerBattery, setIgnoreTrackerBattery] = useState(false);
  const [disableSendSpeedSettings, setDisableSendSpeedSettings] = useState(false);

  const statusesOption = [
    { label: t('scooterModal.statusActiveForRent'), value: ScooterStatus.AVAILABLE, icon: <CheckCircle /> },
    { label: t('scooterModal.statusTransporting'), value: ScooterStatus.TRANSPORTING, icon: <ArrowForward /> },
    { label: t('scooterModal.statusCharging'), value: ScooterStatus.CHARGING, icon: <ElectricBolt /> },
    { label: t('scooterModal.statusMaintenance'), value: ScooterStatus.MAINTENANCE, icon: <Build /> },
    { label: t('scooterModal.statusBrokenInCity'), value: ScooterStatus.BROKEN_IN_CITY, icon: <ErrorOutline /> },
    { label: t('scooterModal.statusLowBattery'), value: ScooterStatus.LOW_BATTERY, icon: <BatteryAlert /> },
    { label: t('scooterModal.statusHidden'), value: ScooterStatus.HIDDEN, icon: <VisibilityOff /> }
  ];

  const isNeedsConfirmForCharging = useMemo(
    () => allowConfirmChangeStatusCity.includes(city) ?? false,
    [city]
  );

  useEffect(() => {
    if (open && scooter) {
      setCity(
        typeof scooter?.city === 'string' ? scooter.city : scooter.city?._id
      );
      setStatus(scooter?.status ?? ScooterStatus.AVAILABLE);
      setIsReplaceableBattery(scooter?.isReplaceableBattery ?? false);
      setIgnoreTrackerBattery(scooter?.ignoreTrackerBattery ?? false);
      setDisableSendSpeedSettings(scooter?.disableSendSpeedSettings ?? false);
    } else {
      setCity('');
      setIsReplaceableBattery(false);
      setIgnoreTrackerBattery(false);
      setDisableSendSpeedSettings(false);
    }
  }, [open, scooter]);

  const citiesOption: SelectOption[] = useMemo(
    () => cities.map(city => ({ label: city.cityName, value: city._id })),
    [cities]
  );

  const updateStatus = (newStatus: ScooterStatus) => {
    if (!scooter?._id) return;

    if (
      isNeedsConfirmForCharging &&
      newStatus === ScooterStatus.CHARGING &&
      !window.confirm(t('scooterModal.chargingConfirm'))
    ) {
      return;
    }

    setStatus(newStatus);
    dispatch(updateScooterStatusAsync(scooter._id, { status: newStatus }) as any);
    onSave();

    if (newStatus === ScooterStatus.BROKEN_IN_CITY && onBrokenStatusChange) {
      onBrokenStatusChange();
    }
  };

  const updateCity = async () => {
    if (!scooter?._id || !city) return;

    const result = await dispatch(updateScooterInfoAsync(scooter._id, { cityId: city }) as any);
    if (result) {
      onSave();
    }
  };

  const updateBatterySettings = async () => {
    if (!scooter?._id) return;

    const result = await dispatch(
      updateScooterInfoAsync(scooter._id, {
        isReplaceableBattery,
        ignoreTrackerBattery,
        disableSendSpeedSettings
      }) as any
    );
    if (result) {
      onSave();
    }
  };

  const getStatusIcon = (statusValue: string) => {
    const statusOption = statusesOption.find(opt => opt.value === statusValue);
    return statusOption?.icon;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('scooterModal.changeStatusTitle')}
            </Typography>

            <Grid container spacing={1}>
              {statusesOption.map((statusOption) => (
                <Grid item xs={6} key={statusOption.value}>
                  <Chip
                    icon={getStatusIcon(statusOption.value)}
                    label={statusOption.label}
                    onClick={() => updateStatus(statusOption.value)}
                    sx={{
                      width: '100%',
                      height: '48px',
                      justifyContent: 'flex-start',
                      bgcolor: status === statusOption.value 
                        ? (theme.palette.mode === 'light' ? '#5073F8' : '#5073F8')
                        : (theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'),
                      color: status === statusOption.value 
                        ? '#000'
                        : (theme.palette.mode === 'light' ? '#000' : '#FFF'),
                      fontWeight: status === statusOption.value ? 600 : 400,
                      '&:hover': {
                        bgcolor: status === statusOption.value 
                          ? (theme.palette.mode === 'light' ? '#4ECDC4' : '#4ECDC4')
                          : (theme.palette.mode === 'light' ? '#D0D0D0' : '#3A3A3A')
                      },
                      '& .MuiChip-icon': {
                        color: status === statusOption.value 
                          ? '#000'
                          : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0')
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('scooterModal.changeCityTitle')}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Select
                value={city}
                onChange={value => setCity(value as string)}
                label=""
                options={citiesOption}
                fullWidth
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={updateCity}
              disabled={!city}
              sx={{
                bgcolor: theme.palette.mode === 'light' ? '#5073F8' : '#5073F8',
                color: '#000',
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'light' ? '#4ECDC4' : '#4ECDC4'
                },
                '&:disabled': {
                  bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                  color: theme.palette.mode === 'light' ? '#9E9E9E' : '#757575'
                }
              }}
            >
              {t('scooterModal.saveCityChanges')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('scooterModal.batterySpeedTitle')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isReplaceableBattery}
                      onChange={(e) => setIsReplaceableBattery(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#5073F8',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#5073F8',
                        },
                      }}
                    />
                  }
                  label={t('scooterModal.replaceableBattery')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ignoreTrackerBattery}
                      onChange={(e) => setIgnoreTrackerBattery(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#5073F8',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#5073F8',
                        },
                      }}
                    />
                  }
                  label={t('scooterModal.fullBattery')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={disableSendSpeedSettings}
                      onChange={(e) => setDisableSendSpeedSettings(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#5073F8',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#5073F8',
                        },
                      }}
                    />
                  }
                  label={t('scooterModal.disableSpeedSettings')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  height: '100%',
                  p: 1,
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'light' ? '#E3F2FD' : '#1E3A5F'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.mode === 'light' ? '#1976D2' : '#90CAF9' }}>
                    {t('scooterModal.chargeLevel', { level: scooter?.trackerBattery !== undefined ? `${scooter.trackerBattery}%` : '-' })}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={updateBatterySettings}
                sx={{
                  bgcolor: theme.palette.mode === 'light' ? '#5073F8' : '#5073F8',
                  color: '#000',
                  fontWeight: 600,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'light' ? '#4ECDC4' : '#4ECDC4'
                  }
                }}
              >
                {t('scooterModal.saveSettings')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsContent;
